import { useCallback, useEffect, useRef, useState } from 'react';
import { errorMessage } from '../lib/errors';
/** Load once on mount (and when `key` changes); discards results that arrive after unmount or a newer load. */
export function useLoad(loader, key = '') {
    const [data, setDataState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const seq = useRef(0);
    const loaderRef = useRef(loader);
    loaderRef.current = loader;
    const reload = useCallback(async () => {
        const mine = ++seq.current;
        setLoading(true);
        setError(null);
        try {
            const result = await loaderRef.current();
            if (mine === seq.current)
                setDataState(result);
            return result;
        }
        catch (err) {
            if (mine === seq.current)
                setError(errorMessage(err));
            return null;
        }
        finally {
            if (mine === seq.current)
                setLoading(false);
        }
    }, []);
    useEffect(() => {
        void reload();
        const counter = seq;
        return () => {
            // Invalidate the in-flight load: its result belongs to an unmounted screen or an old key.
            counter.current++;
        };
    }, [reload, key]);
    const setData = useCallback((updater) => setDataState(updater), []);
    return { data, loading, error, reload, setData };
}
/** A ticking "now" so ages and overdue badges stay current on a screen left open. */
export function useNow(intervalMs = 60000) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const t = window.setInterval(() => setNow(Date.now()), intervalMs);
        return () => window.clearInterval(t);
    }, [intervalMs]);
    return now;
}
